"use client";
import { useCallback, useEffect, useState } from "react"
import { NodeSelection } from "@tiptap/pm/state"

// --- Hooks ---
import { useTiptapEditor } from "@/tiptap/hooks/use-tiptap-editor"

// --- Lib ---
import {
  isExtensionAvailable,
  isNodeTypeSelected,
} from "@/tiptap/lib/tiptap-utils"

// --- Icons ---
import { ImageBorderIcon } from "@/tiptap/icons/image-border-icon"

export const IMAGE_BORDER_STYLES = [
  { label: "Select...", value: "" },
  { label: "Solid", value: "solid" },
  { label: "Dotted", value: "dotted" },
  { label: "Dashed", value: "dashed" },
  { label: "Double", value: "double" },
  { label: "Groove", value: "groove" },
  { label: "Ridge", value: "ridge" },
  { label: "Inset", value: "inset" },
  { label: "Outset", value: "outset" },
  { label: "None", value: "none" },
  { label: "Hidden", value: "hidden" },
]

function parseStyleAttribute(styleAttribute) {
  return (styleAttribute || "")
    .split(";")
    .map((declaration) => declaration.trim())
    .filter(Boolean)
    .reduce((styles, declaration) => {
      const separatorIndex = declaration.indexOf(":")
      if (separatorIndex === -1) return styles

      const propertyName = declaration.slice(0, separatorIndex).trim().toLowerCase()
      const value = declaration.slice(separatorIndex + 1).trim()
      if (!propertyName || !value) return styles

      styles[propertyName] = value
      return styles
    }, {})
}

function serializeStyleAttribute(styles) {
  return Object.entries(styles)
    .filter(([_propertyName, value]) => value !== null && typeof value !== "undefined" && String(value).trim() !== "")
    .map(([propertyName, value]) => `${propertyName}: ${String(value).trim()}`)
    .join("; ")
}

function normalizeBorderWidth(width) {
  const trimmedWidth = String(width || "").trim()
  if (!trimmedWidth) return ""
  if (/^\d+(\.\d+)?$/.test(trimmedWidth)) return `${trimmedWidth}px`
  return ""
}

function stripPixelSuffix(value) {
  return String(value || "").replace(/px$/i, "")
}

function getSelectedImageNode(editor) {
  const selection = editor?.state?.selection
  if (!(selection instanceof NodeSelection)) return null
  if (selection.node.type.name !== "image") return null

  return selection.node
}

export function canSetImageBorder(editor) {
  if (!editor || !editor.isEditable) return false
  if (!isExtensionAvailable(editor, ["image"])) return false

  return isNodeTypeSelected(editor, ["image"]);
}

export function getImageBorderValues(editor) {
  const imageNode = getSelectedImageNode(editor)
  const styles = parseStyleAttribute(imageNode?.attrs?.style || imageNode?.attrs?.["data-mce-style"])

  return {
    borderWidth: stripPixelSuffix(styles["border-width"]),
    borderStyle: styles["border-style"] || "",
  }
}

export function setImageBorder(editor, {
  borderWidth,
  borderStyle,
}) {
  if (!canSetImageBorder(editor)) return false

  const imageNode = getSelectedImageNode(editor)
  if (!imageNode) return false

  const styles = parseStyleAttribute(imageNode.attrs.style || imageNode.attrs["data-mce-style"])
  const nextBorderWidth = normalizeBorderWidth(borderWidth)

  if (nextBorderWidth) {
    styles["border-width"] = nextBorderWidth
  } else {
    delete styles["border-width"]
  }

  if (borderStyle) {
    styles["border-style"] = borderStyle
  } else {
    delete styles["border-style"]
  }

  const nextStyle = serializeStyleAttribute(styles)

  return editor
    .chain()
    .focus()
    .updateAttributes("image", {
      style: nextStyle || null,
      "data-mce-style": nextStyle || null,
    })
    .run();
}

export function shouldShowButton({
  editor,
  hideWhenUnavailable,
}) {
  if (!editor) return false

  if (!hideWhenUnavailable) {
    return true
  }

  return canSetImageBorder(editor);
}

export function useImageBorder(config) {
  const {
    editor: providedEditor,
    hideWhenUnavailable = false,
    onSet,
  } = config || {}

  const { editor } = useTiptapEditor(providedEditor)
  const [isVisible, setIsVisible] = useState(true)
  const [values, setValues] = useState(() => getImageBorderValues(editor))
  const canSet = canSetImageBorder(editor)

  const refreshValues = useCallback(() => {
    setIsVisible(shouldShowButton({ editor, hideWhenUnavailable }))
    setValues(getImageBorderValues(editor))
  }, [editor, hideWhenUnavailable])

  useEffect(() => {
    if (!editor) return

    refreshValues()

    editor.on("selectionUpdate", refreshValues)
    editor.on("transaction", refreshValues)

    return () => {
      editor.off("selectionUpdate", refreshValues)
      editor.off("transaction", refreshValues)
    };
  }, [editor, refreshValues])

  const handleSetBorder = useCallback((nextValues) => {
    if (!editor) return false

    const success = setImageBorder(editor, nextValues)
    if (success) {
      onSet?.()
    }
    return success
  }, [editor, onSet])

  return {
    isVisible,
    canSet,
    values,
    handleSetBorder,
    label: "Image border",
    Icon: ImageBorderIcon,
  }
}
